import { Injectable } from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import * as _ from 'lodash';
import {UrlQueryService} from '../../../../common/url-query/url-query.service';

enum MatchByOptions {
  SRC_AND_DEST = 'Source and Destination Number',
  DEST = 'Destination',
  SOURCE = 'Source'
}

@Injectable({
  providedIn: 'root'
})
export class CallStudioApiService {

  constructor(private http: HttpClient) {}

  private readonly callSetupRuleUrl: string = 'callSetupRule';

  public getCallsItems(): Observable<any> {
    return this.http.get(this.callSetupRuleUrl);
  }

  public getCallsItemsFiltered(params: any = {}): Promise<any> {
    const generatedUrl = UrlQueryService.addQueryParameters(this.callSetupRuleUrl, params);

    return this.http.get(generatedUrl).toPromise();
  }

  public getCallsNames(): Observable<any> {
    return this.getCallsItems().pipe(
      map((res: any) => {
        return res.map(item => {
          return {
            name: item.name,
            id: item.id,
            type: item.type
          };
        });
      })
    );
  }

  public addCallItem(callItem, callItems): Observable<any> {
    callItem.prevId = this.calculatePrevRuleId(callItems, callItem, 'After');
    callItem.nextId = this.calculateNextRuleId(callItems, callItem, 'After');

    return this.http.post(this.callSetupRuleUrl, callItem);
  }

  public editCallItem(callItem): Observable<any> {
    callItem.prevId = -1;
    callItem.nextId = -1;

    return this.http.put(`${this.callSetupRuleUrl}/${callItem.id}`, callItem);
  }

  public moveCallItem(callItem, callItems): Observable<any> {
    const currentCallItem = callItems.find((item) => {
      return item.id === callItem.selectedItemId;
    });

    currentCallItem.prevId = callItem.prevId;
    currentCallItem.nextId = callItem.nextId;

    return this.http.put(`${this.callSetupRuleUrl}/${callItem.selectedItemId}`, currentCallItem);
  }

  public deleteCallItem(itemId): Observable<any> {
    return this.http.delete(`${this.callSetupRuleUrl}/${itemId}`);
  }

  public populateCallItemFields(callItem, externalData): void {
    callItem.nodes = this.findObjectsByIds(callItem.nodeIds, externalData.nodes);
    callItem.PCons = this.findObjectsByIds(callItem.pConIds, externalData.pCons);
    callItem.reroutePCons = this.findObjectsByIds(callItem.reroutePconIds, externalData.pCons);
    callItem.topologyGroups = this.findObjectsByIds(callItem.topologyGroupIds, externalData.resourceGroups);
    callItem.attributeGroupNames = this.findObjectsByIds(callItem.destAttr.attributeGroups, externalData.prefixGroups);
    callItem.srcAttributeGroupNames = this.findObjectsByIds(callItem.srcAttr.attributeGroups, externalData.prefixGroups);
    callItem.attributeValues = callItem.destAttr.attributes;
    callItem.whiteAttributeGroupNames = this.findObjectsByIds(callItem.whiteAttr.attributeGroups, externalData.prefixGroups);
    callItem.whiteAttributeValues = callItem.whiteAttr.attributes;
    callItem.srcAttributeValues = callItem.srcAttr.attributes;
    callItem.searchBy = this.findActionFields(callItem.searchFields, externalData.attributes);
    callItem.replaceWith = this.findActionFields(callItem.actionFields, externalData.attributes);
    callItem.numberPortability = this.findObjectsByIds([callItem.numberPortabilityId], externalData.numberPortability);
    callItem.srcUserGroups = this.findObjectsByIds(callItem.srcUserGroupsIds, externalData.userGroups);
    callItem.dstUserGroups = this.findObjectsByIds(callItem.destUserGroupsIds, externalData.userGroups);
    callItem.pool = this.findObjectsById(callItem.poolId, externalData.prefixGroups);
    callItem.sipConditionGroup = this.findObjectsById(callItem.sipConditionGroupId, externalData.sipConditionGroups);
    callItem.sipManipulationGroup = this.findObjectsById(callItem.sipManipulationGroupId, externalData.sipManipulationGroups);

    if (callItem.numberPortability && callItem.numberPortability[0] &&
        (callItem.numberPortability[0].numberPortabilityAgentType === 'npsb1' ||
        callItem.numberPortability[0].numberPortabilityAgentType === 'hxs')) {
      callItem.directionInfo = _.capitalize(callItem.direction);
    }
    if (callItem.numberPortability && callItem.numberPortability[0] &&
        callItem.numberPortability[0].numberPortabilityAgentType === 'did_masking') {
      callItem.didMatch = MatchByOptions[callItem.didMatchType];
      callItem.directionInfo = _.capitalize(callItem.direction);
    }
    callItem.flowInfo = _.capitalize(callItem.flow.replaceAll('_', ' '));
    if (callItem.srcNormalizationGroupId) {
      callItem.srcNomralizationGroup = externalData.normalizationGroups.find(n => n.id === callItem.srcNormalizationGroupId).groupName;
    }
    if (callItem.destNormalizationGroupId) {
      callItem.destNormalizationGroup = externalData.normalizationGroups.find(n => n.id === callItem.destNormalizationGroupId).groupName;
    }
    if (callItem.continueToRuleId) {
      callItem.continueToRule = externalData.policyStudioRules.find(p => p.id === callItem.continueToRuleId).name;
    }
    if (callItem.sipHeaders) {
      callItem.sipHeadersInfo = this.sipHeadersInitialization(callItem.sipHeaders);
    }
  }

  private findObjectsByIds(idArray, dataArray): any {
    if (this.notValidData(idArray) || this.notValidData(dataArray)) {
      return [];
    }

    const objectArray = [];
    idArray.forEach((id) => {
      const result = this.findObject(id, dataArray);
      if (result) {
        objectArray.push(result);
      }
    });

    return objectArray;
  }

  private findObjectsById(id, dataArray): any {
    if (_.isNil(id) || _.isEmpty(dataArray)) {
      return null;
    }

    return dataArray.find(item => item.id === id);
  }

  private findActionFields(actionsArray, dataArray): any {
    if (!Array.isArray(actionsArray) || actionsArray.length <= 0) {
      return [];
    }

    const actionCount = actionsArray.length;
    const resultArr = [];

    for (let i = 0; i < actionCount; i++) {
      const item: any = {};

      item.name = actionsArray[i].field.replace(/_/g, ' ');
      item.values = [];

      if (actionsArray[i].propertyDicEntryIds !== null) {
        const valuesCount = actionsArray[i].propertyDicEntryIds.length;
        for (let j = 0; j < valuesCount; j++) {
          const obj = this.findObject(actionsArray[i].propertyDicEntryIds[j], dataArray);
          if (obj) {
            item.values.push(obj);
          }
        }
      } else {
        item.values.push(actionsArray[i].sValue);
      }
      resultArr.push(item);
    }

    return resultArr;
  }

  private calculatePrevRuleId(callItems, callItem, position): any {
    if (callItems.length === 0) {
      return -1;
    }

    if (!callItem.id) {
      return callItems[callItems.length - 1].id;
    } else {
      return (position === 'Before') ? this.calculateIdHelper(callItem, callItems, -1) : callItem.id;
    }
  }

  private calculateNextRuleId(callItems, callItem, position): any {
    if (callItems.length === 0) {
      return -1;
    }

    if (!callItem.id) {
      return -1;
    } else {
      return (position === 'After') ? this.calculateIdHelper(callItem, callItems, 1) : callItem.id;
    }
  }

  private calculateIdHelper(callItem, callItems, operator): any {
    const itemsCount = callItems.length;

    for (let i = 0; i < itemsCount; i++) {
      if (callItems[i].id === callItem.id) {
        return (!!callItems[i + operator] ? callItems[i + operator].id : -1);
      }
    }

    return -1;
  }

  private findObject(id, dataArray): any {
    const result = dataArray.find((val) => {
      return id === val.id;
    });

    return result ? result : null;
  }

  private notValidData(value): boolean {
    return !Array.isArray(value) || value.length <= 0;
  }

  private sipHeadersInitialization(sipHeaders): any {
    if (_.isNil(sipHeaders) || sipHeaders.length === 0) { return []; }

    const sipHeadersData = {};
    const matchSipHeaders = [];

    sipHeaders.forEach(sipHeader => {
      if (_.isNil(sipHeadersData[sipHeader.name])) {
        sipHeadersData[sipHeader.name] = [sipHeader.value];
      } else {
        sipHeadersData[sipHeader.name].push(sipHeader.value);
      }
    });

    for (const [name, value] of Object.entries(sipHeadersData)) {
      matchSipHeaders.push({ name, value });
    }

    return matchSipHeaders;
  }
}

